import React, { useEffect, useId, useRef, useState } from "react";
import { m, useInView, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Brain,
  CalendarCheck,
  Check,
  CircleDot,
  ClipboardCheck,
  Database,
  FilePenLine,
  History,
  Languages,
  LockKeyhole,
  MessageSquareText,
  Play,
  ReceiptIndianRupee,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  UserCheck,
} from "lucide-react";
import { Button } from "./ui/primitives";
import { useMarketingMotion } from "./marketing/MarketingMotion";
import { track } from "../lib/analytics";

const WORKFLOW = [
  {
    key: "request",
    label: "Request received",
    eyebrow: "01 · Intent",
    icon: CircleDot,
    title: "Start with a bounded school task",
    description: "Aira identifies the requested outcome and keeps the task inside supported ERP workflows.",
    evidence: "Sample request: prepare a fee reminder for a selected class.",
  },
  {
    key: "scope",
    label: "Scope checked",
    eyebrow: "02 · Permission",
    icon: LockKeyhole,
    title: "Check identity, school and permissions",
    description: "The signed-in user, active school and relevant permissions are checked before supported sources or actions are used.",
    evidence: "Aira exposes permission-checked product context; record-level scope depends on the source tool.",
  },
  {
    key: "evidence",
    label: "Evidence retrieved",
    eyebrow: "03 · Grounding",
    icon: Database,
    title: "Ground the response in connected ERP data",
    description: "Aira uses relevant, permission-shaped product context instead of inventing an operational answer.",
    evidence: "The sample uses selected invoice status and the approved reminder context.",
  },
  {
    key: "draft",
    label: "Draft prepared",
    eyebrow: "04 · Proposal",
    icon: FilePenLine,
    title: "Prepare a reviewable draft",
    description: "For supported actions, Aira proposes the next step and exposes the content or parameters that matter.",
    evidence: "An operator can review eligible guardian and invoice counts, class scope and total due before approving or cancelling.",
  },
  {
    key: "review",
    label: "Human review required",
    eyebrow: "05 · Approval gate",
    icon: UserCheck,
    title: "A person stays in control",
    description: "The workflow pauses here. An authorized operator can review, approve or cancel the proposed action.",
    evidence: "No live message is sent and no school record changes in this marketing demonstration.",
  },
  {
    key: "audit",
    label: "Execution recorded",
    eyebrow: "06 · Evidence",
    icon: ClipboardCheck,
    title: "Execute supported actions and retain evidence",
    description: "After confirmation, an enabled action can run through its permission-checked product route and record the acting user and outcome.",
    evidence: "Outcome evidence belongs in the operational audit trail, not in an opaque AI transcript.",
  },
];

const CAPABILITIES = [
  {
    icon: ShieldCheck,
    title: "Role-scoped context",
    detail: "Existing permissions shape what Aira can retrieve.",
  },
  {
    icon: UserCheck,
    title: "Human approval gates",
    detail: "Material proposals wait for an authorized person.",
  },
  {
    icon: History,
    title: "Action evidence",
    detail: "Supported executions retain acting-user and outcome context.",
  },
  {
    icon: Languages,
    title: "11 language options",
    detail: "English plus 10 Indian languages where configured.",
  },
];

const CHAT_SCENARIOS = [
  {
    key: "fees",
    label: "Fee reminder",
    icon: ReceiptIndianRupee,
    keywords: ["fee", "invoice", "overdue", "payment", "reminder"],
    prompt: "Prepare an overdue-fee reminder for Class XI.",
    answer: "I can prepare a current preview of eligible overdue invoices and guardian recipients, including the recipient count, invoice count and total due. Review the preview, then approve or cancel.",
    facts: ["Fee ledger", "Permission scope", "Recovery safeguard"],
    boundary: "Nothing has been sent.",
    review: {
      scope: "Class XI overdue invoices",
      evidence: "Eligible invoice, guardian and total-due preview",
      authority: "Authorised fees operator",
    },
  },
  {
    key: "attendance",
    label: "Attendance review",
    icon: CalendarCheck,
    keywords: ["attendance", "absent", "present", "marked"],
    prompt: "Review today's attendance.",
    answer: "I can summarise today's saved attendance within your permitted classes: present, absent, total marked and present percentage. If no saved marks are available, I'll say so rather than estimate.",
    facts: ["Attendance register", "Assigned sections", "Selected date"],
    boundary: "This read-only summary does not change attendance records. Marking attendance is a separate permission-checked action that requires confirmation.",
    review: {
      scope: "Permitted classes for the selected day",
      evidence: "Saved attendance marks and status totals",
      authority: "Authorised academic staff",
    },
  },
  {
    key: "admissions",
    label: "Enquiry summary",
    icon: MessageSquareText,
    keywords: ["admission", "enquiry", "enquiries", "funnel", "lead", "crm"],
    prompt: "Summarise our current admissions funnel.",
    answer: "I can summarise current CRM lead counts by recorded stage and the admitted-to-total conversion rate. Open Admissions CRM to review individual enquiries and follow-ups.",
    facts: ["Admissions CRM", "Recorded stages", "Role scope"],
    boundary: "This read-only summary does not rank applicants or change a lead. Converting a lead is a separate permission-checked action that requires confirmation.",
    review: {
      scope: "Current non-archived CRM leads",
      evidence: "Recorded stage counts and conversion summary",
      authority: "Authorised admissions staff",
    },
  },
  {
    key: "tasks",
    label: "My tasks",
    icon: ClipboardCheck,
    keywords: ["task", "tasks", "assigned", "attention", "overdue", "urgent"],
    prompt: "What needs my attention today?",
    answer: "I can show up to 10 open tasks assigned directly to you or to one of your eligible roles, with due time, priority and overdue state. Role-assigned tasks are filtered by branch.",
    facts: ["Task inbox", "Role and branch scope", "Due state"],
    boundary: "This read-only answer does not verify, complete or reassign work.",
    review: {
      scope: "Direct assignments plus branch-eligible tasks for your roles",
      evidence: "Open state, due time, priority and overdue status",
      authority: "Task assignee or authorised supervisor",
    },
  },
];

const GUIDED_BOUNDARY = {
  key: "guided-boundary",
  label: "Guided preview",
  icon: ShieldCheck,
  prompt: "",
  answer: "This public preview can demonstrate fee reminders, today's attendance, the admissions funnel or assigned tasks. Choose a sample prompt; school-specific questions require a signed-in, permission-scoped session.",
  facts: ["Public preview", "No tenant access", "Guided examples"],
  boundary: "No school data was queried.",
  review: {
    scope: "Public marketing sample only",
    evidence: "No connected tenant records",
    authority: "Sign-in required for school context",
  },
};

const ENTER = Object.freeze({ duration: 0.28, ease: [0.23, 1, 0.32, 1] });

function AiraConversationDemo({ reduceMotion, paused }) {
  const chatRef = useRef(null);
  const inView = useInView(chatRef, { amount: 0.45 });
  const [activeKey, setActiveKey] = useState(CHAT_SCENARIOS[0].key);
  const [submittedPrompt, setSubmittedPrompt] = useState(CHAT_SCENARIOS[0].prompt);
  const [draftPrompt, setDraftPrompt] = useState(CHAT_SCENARIOS[0].prompt);
  const [phase, setPhase] = useState(reduceMotion || paused ? "answer" : "checking");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [sampleDecision, setSampleDecision] = useState(null);
  const [documentVisible, setDocumentVisible] = useState(() => (
    typeof document === "undefined" || document.visibilityState === "visible"
  ));
  const active = activeKey === GUIDED_BOUNDARY.key
    ? GUIDED_BOUNDARY
    : CHAT_SCENARIOS.find(({ key }) => key === activeKey) || CHAT_SCENARIOS[0];

  useEffect(() => {
    const updateVisibility = () => setDocumentVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", updateVisibility);
    return () => document.removeEventListener("visibilitychange", updateVisibility);
  }, []);

  useEffect(() => {
    if (reduceMotion || paused) {
      setPhase("answer");
      return undefined;
    }
    if (!inView || !documentVisible || phase === "answer") return undefined;
    const timer = window.setTimeout(() => {
      setPhase((current) => current === "checking" ? "grounding" : "answer");
    }, phase === "checking" ? 240 : 420);
    return () => window.clearTimeout(timer);
  }, [activeKey, documentVisible, inView, paused, phase, reduceMotion, submittedPrompt]);

  const runScenario = (scenario, prompt = scenario.prompt, source = "suggestion") => {
    setActiveKey(scenario.key);
    setSubmittedPrompt(prompt);
    setDraftPrompt(prompt);
    setReviewOpen(false);
    setSampleDecision(null);
    setPhase(reduceMotion || paused ? "answer" : "checking");
    track("aira_sample_prompt_selected", {
      prompt: scenario.key,
      source,
      surface: "aira_marketing_chat",
    });
  };

  const selectScenario = (key) => {
    const scenario = CHAT_SCENARIOS.find((item) => item.key === key) || CHAT_SCENARIOS[0];
    runScenario(scenario);
  };

  const submitPrompt = (event) => {
    event.preventDefault();
    const prompt = draftPrompt.trim();
    if (!prompt) return;
    const normalized = prompt.toLowerCase();
    const scenario = CHAT_SCENARIOS.find((item) => item.keywords.some((keyword) => normalized.includes(keyword))) || GUIDED_BOUNDARY;
    runScenario(scenario, prompt, "composer");
  };

  const statusLabel = phase === "checking"
    ? "Checking role and module access"
    : phase === "grounding"
      ? "Reading permitted ERP context"
      : "Grounded sample ready";
  const approvalRequired = active.key === "fees";

  const decideSample = (decision) => {
    setSampleDecision(decision);
    track("aira_chat_sample_decision", { decision, prompt: active.key, surface: "aira_marketing_chat" });
  };

  return (
    <m.div
      ref={chatRef}
      initial={reduceMotion ? false : { opacity: 0, transform: "translate3d(0, 14px, 0) scale(0.99)" }}
      whileInView={{ opacity: 1, transform: "translate3d(0, 0, 0) scale(1)" }}
      viewport={{ once: true, amount: 0.22 }}
      transition={{ duration: reduceMotion ? 0 : 0.28, ease: [0.23, 1, 0.32, 1] }}
      className="relative mx-auto w-full min-w-0 max-w-[47rem] xl:max-w-none"
      data-testid="aira-chat-window"
    >
      <div className="pointer-events-none absolute -inset-6 rounded-[2.75rem] bg-[radial-gradient(circle_at_72%_16%,rgba(124,58,237,0.28),transparent_46%),radial-gradient(circle_at_22%_82%,rgba(37,99,235,0.24),transparent_48%)] blur-3xl" aria-hidden="true" />
      {/* 2026-08-22: the bezel was a near-black frame (bg-[#07112b]) that read as a hard black
          border around the chat in light theme — now soft purple glass in both themes; the glow
          layer + shadow carry the depth instead. */}
      <div className="relative overflow-hidden rounded-[2rem] border border-[#7c3aed]/20 bg-[#7c3aed]/[0.07] p-2 shadow-[0_34px_90px_-38px_rgba(79,70,229,0.55)]">
        <div className="overflow-hidden rounded-[1.55rem] border border-[#7c3aed]/15 bg-surface">
          <header className="flex items-center gap-3 bg-[linear-gradient(112deg,#174ee4_0%,#493ee8_53%,#7c2ee8_100%)] px-4 py-4 text-white sm:px-5">
            <span className="relative grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-white/25 bg-white/10 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16)]">
              <Brain className="h-5 w-5" aria-hidden="true" />
              <Sparkles className="absolute -right-1 -top-1 h-3.5 w-3.5 text-sky-200" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1" data-testid="aira-chat-phase" data-phase={phase}>
              <p className="font-heading text-base font-bold text-white">Aira AI</p>
              <p
                className="mt-0.5 flex items-center gap-2 text-xs text-white/65"
                role="status"
                aria-live="polite"
                data-testid="aira-chat-status"
                data-motion-state={phase !== "answer" && !reduceMotion && !paused ? "running" : "off"}
                data-phase={phase}
              >
                <span className={`h-2 w-2 rounded-full ${phase === "answer" ? "bg-emerald-300" : "bg-white/80"}`} aria-hidden="true" />
                {statusLabel}
              </p>
            </div>
            <span className="hidden min-h-8 items-center rounded-full border border-white/15 bg-white/[0.07] px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/70 sm:inline-flex">
              No live school records
            </span>
          </header>

          <div className="border-b border-line bg-muted/70 px-3 py-3 sm:px-5">
            <div className="flex snap-x snap-mandatory scroll-px-3 gap-2 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="group" aria-label="Choose an Aira sample question">
              {CHAT_SCENARIOS.map((scenario) => {
                const Icon = scenario.icon;
                const selected = scenario.key === activeKey;
                return (
                  <button
                    key={scenario.key}
                    type="button"
                    onClick={() => selectScenario(scenario.key)}
                    aria-pressed={selected}
                    data-testid={`aira-chat-prompt-${scenario.key}`}
                    className={`inline-flex min-h-11 shrink-0 snap-start items-center gap-2 rounded-full border px-3.5 text-xs font-semibold transition-[background-color,border-color,color,transform] duration-fast ease-premium active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2 ${
                      selected
                        ? "border-[#6d36ea]/35 bg-[linear-gradient(110deg,#2457ea_0%,#4f46e5_52%,#7c3aed_100%)] text-white"
                        : "border-line bg-surface text-ink-2 hover:border-[#6d36ea]/35 hover:text-ink"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                    {scenario.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            className="min-h-[18rem] bg-[radial-gradient(circle_at_82%_8%,rgba(124,58,237,0.07),transparent_36%),linear-gradient(180deg,rgb(var(--vp-bg-muted)/0.5),rgb(var(--vp-bg-surface)))] p-4 sm:min-h-[21rem] sm:p-6"
            role="log"
            aria-live="polite"
            aria-relevant="additions text"
            aria-busy={phase !== "answer"}
            aria-label="Aira sample conversation"
            data-testid="aira-chat-transcript"
          >
            <m.div
              key={`${active.key}-${submittedPrompt}-prompt`}
              initial={reduceMotion ? false : { opacity: 0, transform: "translate3d(8px, 0, 0)" }}
              animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
              transition={{ duration: reduceMotion ? 0 : 0.18, ease: [0.23, 1, 0.32, 1] }}
              className="ml-auto max-w-[88%] rounded-[1.25rem_1.25rem_0.35rem_1.25rem] bg-[linear-gradient(112deg,#2155e8_0%,#5637e8_62%,#7c2ee8_100%)] px-4 py-3 text-sm font-medium leading-6 text-white shadow-[0_14px_32px_-18px_rgba(92,55,226,0.9)] sm:max-w-[78%]"
            >
              {submittedPrompt}
            </m.div>

            <div className="mt-5 flex items-start gap-3">
              <span className="mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-ai/20 bg-ai-soft text-ai-ink">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
              </span>
              {phase !== "answer" ? (
                <m.div
                  key={`${active.key}-${submittedPrompt}-${phase}`}
                  initial={reduceMotion ? false : { opacity: 0, transform: "translate3d(0, 5px, 0)" }}
                  animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
                  transition={{ duration: reduceMotion ? 0 : 0.16, ease: [0.23, 1, 0.32, 1] }}
                  className="min-w-0 rounded-[1.25rem_1.25rem_1.25rem_0.35rem] border border-[#7c3aed]/20 bg-surface px-4 py-3 shadow-card"
                  aria-label={statusLabel}
                  data-testid="aira-chat-typing"
                  data-phase={phase}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="flex gap-1" aria-hidden="true">
                      {[0, 1, 2].map((index) => (
                        <m.span
                          key={index}
                          className="h-1.5 w-1.5 rounded-full bg-ai"
                          initial={reduceMotion ? false : { opacity: 0.3, transform: "translate3d(0, 2px, 0)" }}
                          animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
                          transition={{ duration: reduceMotion ? 0 : 0.16, delay: reduceMotion ? 0 : index * 0.045, ease: [0.23, 1, 0.32, 1] }}
                        />
                      ))}
                    </span>
                    <span className="text-xs font-semibold text-ink-2">{statusLabel}…</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid="aira-chat-grounding-evidence">
                    {(phase === "checking" ? ["User role", "Module access"] : active.facts).slice(0, 3).map((fact) => (
                      <span key={fact} className="rounded-full bg-ai-soft px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-ai-ink">
                        {fact}
                      </span>
                    ))}
                  </div>
                </m.div>
              ) : (
                <m.div
                  key={`${active.key}-${submittedPrompt}-answer`}
                  initial={reduceMotion ? false : { opacity: 0, transform: "translate3d(0, 6px, 0)" }}
                  animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
                  transition={{ duration: reduceMotion ? 0 : 0.2, ease: [0.23, 1, 0.32, 1] }}
                  className="min-w-0 max-w-[92%] sm:max-w-[84%]"
                  data-testid="aira-chat-answer"
                >
                  <div className="rounded-[1.25rem_1.25rem_1.25rem_0.35rem] border border-line bg-surface px-4 py-3.5 shadow-card">
                    <p className="text-sm leading-6 text-ink-2">{active.answer}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5 border-t border-line pt-3" data-testid="aira-chat-evidence">
                      {active.facts.map((fact) => (
                        <span key={fact} className="inline-flex min-h-7 items-center rounded-full bg-ai-soft px-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-ai-ink">
                          {fact}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="mt-2 text-xs font-semibold text-ink-subtle">{active.boundary}</p>
                </m.div>
              )}
            </div>
          </div>

          <div className="bg-surface px-4 pb-4 sm:px-6 sm:pb-6">
            <div
              className={`rounded-2xl border p-4 ${approvalRequired ? "border-caution/30 bg-caution-soft" : "vp-aira-surface"}`}
              data-testid="aira-chat-review-gate"
              data-mode={approvalRequired ? "approval" : "read-only"}
            >
              <div className="flex items-start gap-3">
                <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-surface shadow-card ${approvalRequired ? "text-caution" : "text-ai-ink"}`}>
                  {approvalRequired
                    ? <UserCheck className="h-4 w-4" aria-hidden="true" />
                    : <ShieldCheck className="h-4 w-4" aria-hidden="true" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className={`text-[10px] font-semibold uppercase tracking-[0.17em] ${approvalRequired ? "text-caution" : "text-ai-ink"}`}>
                        {approvalRequired ? "Human checkpoint" : "Control boundary"}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-ink">
                        {approvalRequired ? "Approval required for a supported send" : "Read-only answer · no record changed"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setReviewOpen((open) => !open)}
                      aria-expanded={reviewOpen}
                      aria-controls="aira-chat-review-details"
                      className={`inline-flex min-h-11 shrink-0 items-center justify-center rounded-xl border bg-surface px-3.5 text-xs font-semibold text-ink-2 transition-[background-color,border-color,color,transform] duration-fast ease-premium hover:text-ink active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2 ${approvalRequired ? "border-caution/35 hover:border-caution/55" : "border-[#7c3aed]/25 hover:border-[#7c3aed]/50"}`}
                    >
                      {reviewOpen ? "Hide details" : approvalRequired ? "Review sample action" : "Inspect answer boundary"}
                    </button>
                  </div>
                  {reviewOpen && (
                    <m.ul
                      id="aira-chat-review-details"
                      initial={reduceMotion ? false : { opacity: 0, transform: "translate3d(0, 5px, 0)" }}
                      animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
                      transition={{ duration: reduceMotion ? 0 : 0.18, ease: [0.23, 1, 0.32, 1] }}
                      className={`mt-4 grid gap-2 border-t pt-4 text-xs leading-5 text-ink-2 sm:grid-cols-3 ${approvalRequired ? "border-caution/25" : "border-[#7c3aed]/20"}`}
                    >
                      <li><strong className="block text-ink">Authority</strong> {active.review.authority}</li>
                      <li><strong className="block text-ink">Scope</strong> {active.review.scope}</li>
                      <li><strong className="block text-ink">Evidence</strong> {active.review.evidence}</li>
                    </m.ul>
                  )}
                  {reviewOpen && approvalRequired && (
                    <div className="mt-4 border-t border-caution/25 pt-4" data-testid="aira-chat-action-preview">
                      {sampleDecision ? (
                        <p className="rounded-xl bg-surface px-3.5 py-3 text-xs font-semibold leading-5 text-ink-2" role="status" data-testid="aira-chat-sample-outcome">
                          {sampleDecision === "approved"
                            ? "Sample approved on this page. No live message was sent."
                            : "Sample cancelled. No live message was sent."}
                        </p>
                      ) : (
                        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                          <button type="button" onClick={() => decideSample("cancelled")} className="inline-flex min-h-11 items-center justify-center rounded-xl border border-line bg-surface px-3.5 text-xs font-semibold text-ink-2 transition-[background-color,border-color,transform] duration-fast ease-premium hover:border-line-strong active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2" data-testid="aira-chat-cancel-sample">
                            Cancel sample
                          </button>
                          <button type="button" onClick={() => decideSample("approved")} className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[linear-gradient(110deg,#2457ea_0%,#4f46e5_52%,#7c3aed_100%)] px-3.5 text-xs font-semibold text-white shadow-[0_8px_20px_-12px_rgba(92,55,226,0.85)] transition-[filter,transform] duration-fast ease-premium hover:brightness-105 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2" data-testid="aira-chat-approve-sample">
                            Approve sample
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <form
            className="flex items-center gap-2.5 border-t border-line bg-surface p-3 sm:gap-3 sm:p-4"
            aria-label="Try a guided Aira question"
            data-testid="aira-chat-composer"
            onSubmit={submitPrompt}
          >
            <div className="flex min-h-12 min-w-0 flex-1 items-center gap-2.5 rounded-xl border border-line bg-muted/45 px-3.5 focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-focusring/35">
              <Sparkles className="h-4 w-4 shrink-0 text-ai-ink" aria-hidden="true" />
              <input
                type="text"
                value={draftPrompt}
                onChange={(event) => setDraftPrompt(event.target.value)}
                maxLength={160}
                autoComplete="off"
                aria-label="Ask a guided Aira question"
                aria-describedby="aira-chat-composer-help"
                className="min-h-11 min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-subtle"
                placeholder="Try a fee, attendance, admissions or task question"
                data-testid="aira-chat-input"
              />
            </div>
            <button
              type="submit"
              disabled={!draftPrompt.trim()}
              aria-label="Run guided Aira preview"
              className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-[#2155e8] to-[#7c3aed] text-white shadow-[0_10px_24px_-12px_rgba(86,50,220,0.85)] transition-[filter,transform] duration-fast ease-premium hover:brightness-105 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-45 disabled:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2"
              data-testid="aira-chat-submit"
            >
              <Send className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
          <p id="aira-chat-composer-help" className="sr-only">
            This guided public preview recognises fee, attendance, admissions and assigned-task examples. It does not query live school data.
          </p>
        </div>
      </div>
      <p className="mt-3 text-center text-[11px] font-semibold tracking-wide text-ink-subtle">
        School-workflow sample context <span aria-hidden="true">·</span> the live assistant uses public product and Help Center content
      </p>
    </m.div>
  );
}

function GovernanceStep({ step, index, activeIndex, onSelect, panelId, disabled = false }) {
  const Icon = step.icon;
  const active = index === activeIndex;
  const complete = index < activeIndex;

  return (
    <li className="relative min-w-[12.25rem] snap-start lg:min-w-0">
      {index < WORKFLOW.length - 1 && (
        <span
          className={`absolute left-[1.375rem] top-[3.8rem] hidden h-[calc(100%-2.55rem)] w-px lg:block ${complete ? "bg-sky-300" : "bg-white/10"}`}
          aria-hidden="true"
        />
      )}
      <button
        type="button"
        onClick={() => onSelect(index)}
        disabled={disabled}
        aria-current={active ? "step" : undefined}
        aria-controls={panelId}
        data-testid={`aira-stage-${step.key}`}
        className={`group relative grid min-h-[4.25rem] w-full grid-cols-[2.75rem_minmax(0,1fr)] items-center gap-3 rounded-xl border px-2 py-2 text-left transition-[background-color,border-color,color,transform] duration-fast ease-premium active:scale-[0.985] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200 focus-visible:ring-offset-2 focus-visible:ring-offset-[#09275a] disabled:cursor-not-allowed disabled:opacity-45 disabled:active:scale-100 ${
          active
            ? "border-sky-200/35 bg-white/[0.12]"
            : "border-transparent hover:border-white/10 hover:bg-white/[0.055] disabled:hover:border-transparent disabled:hover:bg-transparent"
        }`}
      >
        <span
          className={`relative z-[1] grid h-11 w-11 place-items-center rounded-xl border transition-[background-color,border-color,color] duration-fast ease-premium ${
            complete
              ? "border-emerald-300/35 bg-emerald-300/15 text-emerald-200"
              : active
                ? "border-sky-200/45 bg-sky-300/15 text-sky-100"
                : "border-white/10 bg-white/[0.035] text-white/50 group-hover:text-white/80"
          }`}
        >
          {complete ? <Check className="h-4 w-4" aria-hidden="true" /> : <Icon className="h-4 w-4" aria-hidden="true" />}
        </span>
        <span className="min-w-0">
          <span className={`block text-[10px] font-semibold uppercase tracking-[0.18em] ${active ? "text-sky-100" : "text-white/55"}`}>
            {step.eyebrow}
          </span>
          <span className={`mt-1 block text-sm font-semibold leading-5 ${active ? "text-white" : "text-white/70"}`}>
            {step.label}
          </span>
        </span>
      </button>
    </li>
  );
}

function GovernedWorkflow({ reduceMotion, paused, labelledBy }) {
  const panelId = useId();
  const stageRef = useRef(null);
  const inView = useInView(stageRef, { amount: 0.32 });
  const timerRef = useRef(null);
  const manuallyControlledRef = useRef(false);
  const [documentVisible, setDocumentVisible] = useState(() => (
    typeof document === "undefined" || document.visibilityState === "visible"
  ));
  const [attentionHeld, setAttentionHeld] = useState(false);
  const [activeIndex, setActiveIndex] = useState(reduceMotion || paused ? 4 : 0);
  const [sampleApproved, setSampleApproved] = useState(false);
  const [replayToken, setReplayToken] = useState(0);

  useEffect(() => {
    const updateVisibility = () => setDocumentVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", updateVisibility);
    return () => document.removeEventListener("visibilitychange", updateVisibility);
  }, []);

  useEffect(() => {
    if (!inView || !documentVisible || attentionHeld || reduceMotion || paused || manuallyControlledRef.current || activeIndex >= 4) return undefined;
    timerRef.current = window.setTimeout(
      () => setActiveIndex((current) => Math.min(current + 1, 4)),
      activeIndex === 0 ? 1900 : 2300,
    );
    return () => window.clearTimeout(timerRef.current);
  }, [activeIndex, attentionHeld, documentVisible, inView, paused, reduceMotion, replayToken]);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const selectStep = (index) => {
    window.clearTimeout(timerRef.current);
    manuallyControlledRef.current = true;
    setActiveIndex(index);
    if (index !== 5) setSampleApproved(false);
  };

  const restart = () => {
    window.clearTimeout(timerRef.current);
    manuallyControlledRef.current = false;
    setSampleApproved(false);
    setActiveIndex(0);
    setReplayToken((token) => token + 1);
  };

  const approveSample = () => {
    window.clearTimeout(timerRef.current);
    manuallyControlledRef.current = true;
    setSampleApproved(true);
    setActiveIndex(5);
    track("aira_governance_sample_approved", { surface: "aira_governance_workflow" });
  };

  const activeStep = WORKFLOW[activeIndex];
  const progress = (activeIndex + 1) / WORKFLOW.length;
  const approvalStage = activeStep.key === "review" && !sampleApproved;
  const status = approvalStage ? "Awaiting review" : sampleApproved ? "Sample approved" : "Review gate ahead";

  return (
    <div
      ref={stageRef}
      className="overflow-hidden rounded-[1.75rem] border border-primary/20 bg-surface shadow-[0_28px_72px_-40px_rgba(29,78,216,0.42)] dark:shadow-[0_34px_90px_-42px_rgba(56,189,248,0.32)]"
      aria-labelledby={labelledBy}
      data-testid="aira-governed-workflow"
      onMouseEnter={() => setAttentionHeld(true)}
      onMouseLeave={() => setAttentionHeld(false)}
      onFocusCapture={() => setAttentionHeld(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setAttentionHeld(false);
      }}
    >
      <div className="bg-[#092a5d] text-white dark:bg-[#071c37]">
        <div className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-sky-200/25 bg-sky-300/10 text-sky-100">
              <Brain className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white">Aira governance path</p>
              <p className="mt-0.5 text-xs text-white/60">Illustrative workflow · sample data</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/55">
              Stage {String(activeIndex + 1).padStart(2, "0")} / {String(WORKFLOW.length).padStart(2, "0")}
            </span>
            <span className={`inline-flex min-h-8 items-center gap-2 rounded-full border px-3 text-xs font-semibold ${
              approvalStage
                ? "border-amber-200/25 bg-amber-200/10 text-amber-100"
                : sampleApproved
                  ? "border-emerald-200/25 bg-emerald-200/10 text-emerald-100"
                  : "border-sky-200/25 bg-sky-200/10 text-sky-100"
            }`}>
              <span className={`h-2 w-2 rounded-full ${approvalStage ? "bg-amber-300" : sampleApproved ? "bg-emerald-300" : "bg-sky-300"}`} aria-hidden="true" />
              {status}
            </span>
          </div>
        </div>
        <div
          className="h-1 overflow-hidden bg-white/10"
          role="progressbar"
          aria-label="Aira governance workflow progress"
          aria-valuemin="1"
          aria-valuemax={WORKFLOW.length}
          aria-valuenow={activeIndex + 1}
        >
          <m.span
            className="block h-full w-full origin-left bg-sky-300"
            animate={{ transform: `scaleX(${progress})` }}
            transition={reduceMotion || paused ? { duration: 0 } : { duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
          />
        </div>
      </div>

      <div className="grid min-w-0 lg:grid-cols-[0.82fr_1.18fr]">
        <ol
          className="flex min-w-0 snap-x gap-2 overflow-x-auto overscroll-x-contain border-b border-white/10 bg-[#09275a] p-3 [scrollbar-color:rgba(125,211,252,0.45)_transparent] [scrollbar-width:thin] dark:bg-[#071a34] sm:p-4 lg:block lg:space-y-1 lg:overflow-visible lg:border-b-0 lg:border-r lg:p-5"
          aria-label="Aira workflow stages"
          data-testid="aira-workflow-stages"
        >
          {WORKFLOW.map((step, index) => (
            <GovernanceStep
              key={step.key}
              step={step}
              index={index}
              activeIndex={activeIndex}
              onSelect={selectStep}
              panelId={panelId}
              disabled={index === 5 && !sampleApproved}
            />
          ))}
        </ol>

        <div className="min-w-0 bg-surface p-5 sm:p-7 lg:p-8" id={panelId}>
          <div className="flex items-start gap-3 rounded-2xl border border-primary/15 bg-primary-50 p-4">
            <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent/10 text-accent-active">
              <CircleDot className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-accent-active">Sample operational request</p>
              <p className="mt-1.5 text-sm font-semibold leading-6 text-ink">“Prepare an overdue-fee reminder for Class XI.”</p>
            </div>
          </div>

          <m.div
            key={activeStep.key}
            initial={reduceMotion || paused ? false : { opacity: 0, transform: "translate3d(0, 6px, 0)" }}
            animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
            transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
            className="mt-7 min-h-[14rem]"
            data-testid="aira-active-stage"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-active">{activeStep.eyebrow}</p>
            <h4 className="mt-3 max-w-xl font-heading text-2xl font-bold leading-tight tracking-[-0.025em] text-ink sm:text-[2rem]">{activeStep.title}</h4>
            <p className="mt-4 max-w-xl text-[15px] leading-7 text-ink-2">{activeStep.description}</p>
            <div className={`mt-6 flex items-start gap-3 rounded-2xl border p-4 ${
              approvalStage ? "border-caution/30 bg-caution-soft" : "border-line bg-muted/60"
            }`}>
              {approvalStage
                ? <UserCheck className="mt-0.5 h-4 w-4 shrink-0 text-caution" aria-hidden="true" />
                : <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent-active" aria-hidden="true" />}
              <div className="min-w-0">
                <p className={`text-[10px] font-semibold uppercase tracking-[0.16em] ${approvalStage ? "text-caution" : "text-accent-active"}`}>
                  {approvalStage ? "Human checkpoint" : "Control evidence"}
                </p>
                <p className="mt-1 text-sm leading-6 text-ink-2">{activeStep.evidence}</p>
              </div>
            </div>
          </m.div>

          <div className="mt-5 flex flex-col gap-3 border-t border-line pt-5 sm:flex-row sm:flex-wrap sm:items-center">
            {approvalStage ? (
              <button
                type="button"
                onClick={approveSample}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-fg shadow-[0_8px_20px_-10px_rgba(29,78,216,0.7)] transition-[background-color,box-shadow,transform] duration-fast ease-premium hover:bg-primary-700 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2 sm:w-auto"
              >
                <UserCheck className="h-4 w-4" aria-hidden="true" /> Review and approve sample
              </button>
            ) : (
              <button
                type="button"
                onClick={restart}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-line bg-surface px-4 text-sm font-semibold text-ink-2 transition-[background-color,border-color,color,transform] duration-fast ease-premium hover:border-line-strong hover:bg-muted active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2 sm:w-auto"
              >
                {activeIndex === 0 ? <Play className="h-4 w-4" aria-hidden="true" /> : <RefreshCw className="h-4 w-4" aria-hidden="true" />}
                Restart walkthrough
              </button>
            )}
            <p className="max-w-md text-xs leading-5 text-ink-subtle">This control advances a sample only. It does not contact a parent or alter a live record.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AiraAISection({ onTry, onDemo }) {
  const osReducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const reduceMotion = Boolean(osReducedMotion || paused);
  const headingId = useId();
  const workflowHeadingId = useId();

  return (
    <section
      id="ai"
      aria-labelledby={headingId}
      className="relative scroll-mt-24 overflow-hidden border-y border-line campus-glass-primary py-20 text-ink sm:py-24 lg:py-28"
      data-testid="aira-premium-section"
    >
      <div
        className="pointer-events-none absolute inset-0 dark:hidden"
        style={{
          // 2026-08-22: the base wash dropped from ~0.93 to ~0.5 alpha — at 0.96/0.9 it walled off
          // the scenic scroll stage behind the section; the purple radials keep the Aira cast.
          backgroundImage: "radial-gradient(circle at 78% 15%, rgba(124,58,237,0.17), transparent 31%), radial-gradient(circle at 12% 43%, rgba(37,99,235,0.12), transparent 33%), linear-gradient(180deg, rgba(251,252,255,0.55) 0%, rgba(244,242,255,0.45) 100%)",
        }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 hidden dark:block"
        style={{
          backgroundImage: "radial-gradient(circle at 77% 14%, rgba(139,92,246,0.24), transparent 34%), radial-gradient(circle at 9% 48%, rgba(37,99,235,0.18), transparent 35%), linear-gradient(180deg, #070b18 0%, #0b1024 100%)",
        }}
        aria-hidden="true"
      />
      <div className="pointer-events-none absolute -right-24 -top-36 h-[30rem] w-[30rem] rounded-full bg-sky/10 blur-[120px]" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-32 top-[36rem] h-[28rem] w-[28rem] rounded-full bg-primary/10 blur-[130px]" aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-6 lg:px-8">
        <div
          className="grid min-w-0 gap-12 xl:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)] xl:items-center xl:gap-20"
          data-testid="aira-governance-intro"
        >
          <m.div
            initial={reduceMotion ? false : { opacity: 0, transform: "translate3d(0, 12px, 0)" }}
            whileInView={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
            viewport={{ once: true, amount: 0.35 }}
            transition={ENTER}
            className="min-w-0"
          >
            <span className="inline-flex min-h-8 items-center gap-2 rounded-full border border-primary/20 bg-surface/80 px-3 text-xs font-semibold tracking-wide text-accent-active shadow-card">
              <Brain className="h-3.5 w-3.5" aria-hidden="true" /> Aira · governed school intelligence
            </span>
            <h2 id={headingId} className="mt-5 max-w-[13ch] text-balance font-heading text-[clamp(2.65rem,4.5vw,4.15rem)] font-bold leading-[0.98] tracking-[-0.055em] text-ink">
              <span className="block">Ask Aira AI.{" "}</span>
              <span className="mt-1 block bg-[linear-gradient(105deg,#2155e8_8%,#7c3aed_88%)] bg-clip-text text-transparent dark:bg-[linear-gradient(105deg,#93c5fd_8%,#c4b5fd_88%)]">Get grounded answers.</span>
            </h2>
            <p className="mt-6 max-w-[34rem] text-pretty text-base leading-7 text-ink-2 sm:text-lg sm:leading-8">
              Choose a school workflow. Aira uses tenant- and permission-checked product context, and separates read-only answers from action proposals that require confirmation.
            </p>

            <div className="mt-7 flex flex-wrap gap-2" aria-label="Aira workflow examples">
              <span className="inline-flex min-h-9 items-center gap-2 rounded-xl border border-line bg-surface px-3 text-xs font-semibold text-ink-2 shadow-card">
                <ReceiptIndianRupee className="h-3.5 w-3.5 text-accent-active" aria-hidden="true" /> Fee context
              </span>
              <span className="inline-flex min-h-9 items-center gap-2 rounded-xl border border-line bg-surface px-3 text-xs font-semibold text-ink-2 shadow-card">
                <CalendarCheck className="h-3.5 w-3.5 text-accent-active" aria-hidden="true" /> Attendance review
              </span>
              <span className="inline-flex min-h-9 items-center gap-2 rounded-xl border border-line bg-surface px-3 text-xs font-semibold text-ink-2 shadow-card">
                <MessageSquareText className="h-3.5 w-3.5 text-accent-active" aria-hidden="true" /> Enquiry follow-up
              </span>
              <span className="inline-flex min-h-9 items-center gap-2 rounded-xl border border-line bg-surface px-3 text-xs font-semibold text-ink-2 shadow-card">
                <ClipboardCheck className="h-3.5 w-3.5 text-accent-active" aria-hidden="true" /> Task inbox
              </span>
            </div>

            <div className="mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <Button
                variant="primary"
                onClick={() => {
                  track("aira_demo_interact", { surface: "ai_live_assistant_cta" });
                  if (onTry) onTry();
                  else window.dispatchEvent(new CustomEvent("vp-aira-open"));
                }}
                data-testid="aira-try"
                className="w-full justify-center !bg-[linear-gradient(110deg,#2457ea_0%,#4f46e5_52%,#7c3aed_100%)] !text-white shadow-[0_14px_30px_-14px_rgba(92,55,226,0.78)] hover:brightness-105 sm:w-auto"
              >
                Ask the live product assistant <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Button>
              <button
                type="button"
                onClick={onDemo}
                data-testid="aira-demo"
                className="inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-primary/25 bg-surface px-4 text-sm font-semibold text-accent-active transition-[background-color,border-color,color,transform] duration-fast ease-premium hover:border-primary/40 hover:bg-primary-50 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2 sm:w-auto"
              >
                Book a live demo
              </button>
            </div>

            <div className="mt-8 flex items-start gap-3 border-t border-primary/15 pt-5">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-accent/10 text-accent-active">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-accent-active">School-controlled boundary</p>
                <p className="mt-1.5 max-w-md text-sm leading-6 text-ink-2">
                  Your role, enabled modules and configured providers remain the operating boundary. Aira does not become the authority.
                </p>
              </div>
            </div>
          </m.div>

          <AiraConversationDemo reduceMotion={reduceMotion} paused={paused} />
        </div>

        <m.ul
          initial={reduceMotion ? false : { opacity: 0, transform: "translate3d(0, 8px, 0)" }}
          whileInView={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.24, delay: reduceMotion ? 0 : 0.1, ease: [0.23, 1, 0.32, 1] }}
          className="mt-14 grid gap-x-8 border-y border-primary/15 py-5 sm:grid-cols-2 lg:grid-cols-4 lg:py-6"
          data-testid="aira-governance-capabilities"
        >
          {CAPABILITIES.map(({ icon: Icon, title, detail }) => (
            <li key={title} className="flex min-w-0 items-start gap-3 py-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-accent/10 text-accent-active">
                <Icon className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-ink">{title}</span>
                <span className="mt-1 block text-xs leading-5 text-ink-subtle">{detail}</span>
              </span>
            </li>
          ))}
        </m.ul>

        <div className="mt-14 grid min-w-0 gap-4 lg:grid-cols-[0.82fr_1.18fr] lg:items-end lg:gap-14">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-active">Interactive governance path</p>
            <h3 id={workflowHeadingId} className="mt-3 max-w-2xl text-balance font-heading text-2xl font-bold tracking-[-0.03em] text-ink sm:text-[2rem]">
              Follow one request to the human checkpoint.
            </h3>
          </div>
          <p className="max-w-2xl text-sm leading-6 text-ink-2 sm:text-[15px] sm:leading-7">
            Choose any stage, or let the short guided sequence advance while the section is visible. It stops before execution and waits for an explicit sample approval.
          </p>
        </div>

        <m.div
          initial={reduceMotion ? false : { opacity: 0, transform: "translate3d(0, 12px, 0)" }}
          whileInView={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
          viewport={{ once: true, amount: 0.16 }}
          transition={{ ...ENTER, delay: reduceMotion ? 0 : 0.08 }}
          className="mt-7"
        >
          <GovernedWorkflow reduceMotion={reduceMotion} paused={paused} labelledBy={workflowHeadingId} />
        </m.div>

        <div className="mt-10 flex flex-col justify-between gap-6 border-t border-primary/15 pt-7 lg:flex-row lg:items-center">
          <div className="max-w-2xl">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-accent-active">Deployment boundary</p>
            <p className="mt-2 text-sm leading-6 text-ink-2">
              Availability depends on the enabled module, user permission and configured channel. Drafted communication remains reviewable before a supported send action.
            </p>
          </div>
          <p className="max-w-lg text-xs leading-5 text-ink-subtle lg:text-right">
            Interactive samples illustrate the control model; they do not read live tenant data or execute a live action.
          </p>
        </div>
      </div>
    </section>
  );
}
